import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { selectUser } from "@/store/auth/auth.slice";
import { useThunkSelector } from "@/store/hooks";
import { currentPropertySelector, experienceMediaItemsSelector } from "@/store/selectors";
import { currentOrganizationSelector } from "@/store/v2/currentOrganization/currentOrganizationSlice";
import { isPublicPage } from "@/utils/visualBuilder";

import { MEDIA_SIZE, REQUEST_STATUSES, TAB_STATUSES } from "../../constants";
import { useUrlParams } from "../../hooks";
import { ViewService } from "../../services";
import { resetVisualBuilderData, updateVisualBuilderData } from "../../store/actions";
import { getMediaLink } from "../../utils/mediaLink";
import { handleTab } from "../components/GlobalTabs/GlobalTabsDataSlice";

export function useFullscreen(props) {
  const { thumbnailsPlay, currentMediaItemIndex, filteredMediaItems = [] } = props;

  const navigate = useNavigate();
  const params = useUrlParams();
  const { propertyId, experienceId = null, tabStatus } = params;

  const dispatch = useDispatch();

  const { property } = useSelector(currentPropertySelector);
  const user = useThunkSelector(selectUser);
  const { experienceMediaItems = {}, status: experienceMediaItemsReqStatus } = useSelector(
    experienceMediaItemsSelector(propertyId, experienceId),
  );

  const { id: userId } = user;
  const experienceMediaItemsLength = Object.keys(experienceMediaItems).length;

  // State to track when all effects are done
  const [isReady, setIsReady] = useState(false);
  const [effect1Done, setEffect1Done] = useState(false);
  const [effect2Done, setEffect2Done] = useState(false);
  const [effect3Done, setEffect3Done] = useState(false);
  const [effect4Done, setEffect4Done] = useState(false);

  const { data: organization } = useSelector(currentOrganizationSelector);
  const organizationId = organization?.id;
  const isOrganizationsSection = location.pathname.startsWith("/organizations");
  const tabPrefix = isOrganizationsSection ? `/organizations/${organizationId}` : "";

  useEffect(() => {
    if (isPublicPage(tabStatus)) {
      const favicon = document.getElementById("favicon");

      const { logoFavicon } = property || {};
      document.title = property.name;

      if (logoFavicon) {
        favicon.href = getMediaLink({ size: MEDIA_SIZE.small, link: logoFavicon });
      }
    }
    setEffect1Done(true);
  }, [property, tabStatus]);

  useEffect(() => {
    (async () => {
      if (propertyId && userId && experienceId && tabStatus !== TAB_STATUSES.SHARE) {
        await ViewService.addView(propertyId, experienceId);
      }
      setEffect2Done(true);
    })();
  }, [propertyId, userId, experienceId]);

  useEffect(() => {
    const toggle = setTimeout(() => {
      if (![TAB_STATUSES.CUSTOMIZE, TAB_STATUSES.SHARE].includes(tabStatus)) {
        dispatch(updateVisualBuilderData(params, { carousel: false }));
      }
      setEffect3Done(true);
    }, 2000);

    return () => clearTimeout(toggle);
  }, [dispatch, tabStatus]);

  useEffect(() => {
    if (
      params.mode === "visualization" &&
      [TAB_STATUSES.CUSTOMIZE, TAB_STATUSES.SHARE].includes(tabStatus) &&
      experienceMediaItemsReqStatus === REQUEST_STATUSES.SUCCEEDED &&
      experienceMediaItemsLength === 0
    ) {
      dispatch(resetVisualBuilderData(params));
      const newState = {
        status: "active",
        name: "Loading",
      };
      const tabId = `${tabPrefix}/properties/${propertyId}/experiences/${experienceId}/collect`;
      dispatch(handleTab({ userId, tabId, newState }));
      navigate(tabId);
    }
    setEffect4Done(true);
  }, [experienceMediaItemsLength, tabStatus, experienceMediaItemsReqStatus]);

  const dispatchSlide = (slide) => {
    dispatch(
      updateVisualBuilderData(params, {
        currentMediaItemId: slide.id,
      }),
    );
  };

  const timer2 = useRef(null); // we can save timer in useRef and pass it to child

  const nextSlide = filteredMediaItems[currentMediaItemIndex + 1]
    ? filteredMediaItems[currentMediaItemIndex + 1]
    : filteredMediaItems[0];

  useEffect(() => {
    if (thumbnailsPlay) {
      timer2.current = setInterval(() => dispatchSlide(nextSlide), 3000);
    }
    return () => {
      clearInterval(timer2.current);
    };
  });

  useEffect(() => {
    if (effect1Done && effect2Done && effect3Done && effect4Done) {
      setIsReady(true);
    }
  }, [effect1Done, effect2Done, effect3Done, effect4Done]);

  return { isReady };
}
